import { useState, useMemo } from 'react';
import { ArrowLeft, Copy, Check, Trash2, Key, Clock, User, Sparkles } from 'lucide-react';
import { Email } from '@/types/email';
import { Button } from '@/components/ui/button';
import { AdBanner } from '@/components/AdBanner';
import { toast } from 'sonner';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';

interface EmailViewerProps {
  email: Email;
  onBack: () => void;
  onDelete: (id: string) => void;
}

export const EmailViewer = ({ email, onBack, onDelete }: EmailViewerProps) => {
  const [otpCopied, setOtpCopied] = useState(false);

  // Sanitize HTML content to prevent XSS
  const sanitizedHtml = useMemo(() => {
    if (!email.bodyHtml) return null;
    return DOMPurify.sanitize(email.bodyHtml, {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'a', 'ul', 'ol', 'li', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img', 'blockquote', 'pre', 'code', 'hr'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target', 'rel', 'width', 'height'],
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    });
  }, [email.bodyHtml]);

  const copyOTP = async () => {
    if (!email.otp) return;
    try {
      await navigator.clipboard.writeText(email.otp);
      setOtpCopied(true);
      toast.success('OTP copied to clipboard');
      setTimeout(() => setOtpCopied(false), 2000);
    } catch {
      toast.error('Failed to copy OTP');
    }
  };

  const handleDelete = () => {
    onDelete(email.id);
    toast.success('Email deleted');
    onBack();
  };

  return (
    <div className="flex flex-col h-full animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border/40">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 rounded-xl hover:bg-secondary">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Back</span>
        </Button>

        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleDelete} 
          className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Email Content */}
      <div className="flex-1 overflow-y-auto p-6 lg:p-8">
        {/* Subject */}
        <h1 className="text-2xl lg:text-3xl font-bold mb-6 text-balance leading-tight">{email.subject}</h1>

        {/* OTP Highlight */}
        {email.hasOTP && email.otp && (
          <div className="mb-8 p-6 rounded-3xl gradient-bg shadow-glow animate-scale-in relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer" />
            <div className="flex items-center justify-between relative">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary-foreground/20 flex items-center justify-center backdrop-blur-sm">
                  <Key className="w-7 h-7 text-primary-foreground" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-primary-foreground/80 uppercase tracking-widest font-semibold">
                      Verification Code
                    </p>
                    <Sparkles className="w-3 h-3 text-primary-foreground/60" />
                  </div>
                  <p className="text-4xl lg:text-5xl font-mono font-black text-primary-foreground tracking-[0.2em]">
                    {email.otp}
                  </p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="lg"
                onClick={copyOTP}
                className="bg-primary-foreground/15 hover:bg-primary-foreground/25 text-primary-foreground border-0 rounded-xl backdrop-blur-sm"
              >
                {otpCopied ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-2" />
                    Copy Code
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Sender Info */}
        <div className="flex items-start gap-4 mb-8 pb-6 border-b border-border/40">
          <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center">
            <span className="text-xl font-bold text-secondary-foreground">
              {email.fromName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-bold">{email.fromName}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="truncate">{email.from}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{format(email.receivedAt, 'PPpp')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Email Body */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          {sanitizedHtml ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          ) : (
            email.body.split('\n').map((line, index) => (
              <p key={index} className={line === '' ? 'h-4' : 'leading-relaxed'}>
                {line}
              </p>
            ))
          )}
        </div>

        {/* Ad Banner at bottom of email */}
        <div className="mt-8 pt-6 border-t border-border/40">
          <div className="rounded-xl bg-secondary/30 border border-border/30 p-2 flex justify-center overflow-hidden">
            <AdBanner />
          </div>
        </div>
      </div>
    </div>
  );
};